import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Alert } from './ui';
import styles from '@/styles/components/OTPVerification.module.css';

/**
 * OTPVerification component props interface
 */
export interface OTPVerificationProps {
  /** Function to call when OTP is verified */
  onVerify: (otp: string) => Promise<void>;
  /** Function to call when resend is requested */
  onResend: () => Promise<void>;
  /** Whether the verification is in loading state */
  isLoading?: boolean;
  /** Error message to display */
  error?: string;
  /** Action being performed (for context) */
  action?: string;
  /** Title for the verification form */
  title?: string;
  /** Description text */
  description?: string;
}

/**
 * OTPVerification component following design system standards
 * Uses standardized UI components for consistent styling
 * 
 * @example
 * ```tsx
 * <OTPVerification 
 *   onVerify={handleVerify}
 *   onResend={handleResend}
 *   isLoading={isLoading}
 *   error={error}
 *   title="Verify Email"
 * />
 * ```
 */
const OTPVerification: React.FC<OTPVerificationProps> = ({
  onVerify,
  onResend,
  isLoading = false,
  error,

  title = "Verify your email",
  description = "We've sent a 6-digit verification code to your email address"
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Auto-focus first input
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    // Countdown for resend cooldown
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendCooldown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text/plain')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp([...newOtp, ...Array(6 - newOtp.length).fill('')]);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setOtpError('Please enter the complete 6-digit code');
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      setOtpError('Please enter only numbers');
      return;
    }

    try {
      await onVerify(otpString);
    } catch (error: unknown) {
      setOtpError((error as Error).message || 'Verification failed');
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setResendLoading(true);
    try {
      await onResend();
      setResendCooldown(60); // 60 second cooldown
    } catch (error: unknown) {
      console.error('Resend failed:', error);
    } finally {
      setResendLoading(false);
    }
  };



  const currentError = otpError || error;

  return (
    <div className={styles.container}>
      <Card variant="elevated" padding="lg">
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.description}>{description}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* OTP Input Section */}
          <div className={styles.otpSection}>
            <label className={styles.otpLabel}>
              Verification Code
            </label>
            
            <div className={styles.otpInputContainer}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={el => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={styles.otpInput}
                  disabled={isLoading}
                />
              ))}
            </div>

            {currentError && (
              <Alert variant="danger" style={{ textAlign: 'center' }}>
                {currentError}
              </Alert>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isLoading}
            disabled={isLoading || otp.join('').length !== 6}
            className={styles.submitButton}
          >
            {isLoading ? 'Verifying...' : 'Verify Code'}
          </Button>
        </form>

        {/* Resend Section */}
        <div className={styles.resendSection}>
          <p className={styles.resendText}>
            Didn't receive the code?
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendLoading || resendCooldown > 0}
            className={styles.resendButton}
          >
            {resendLoading
              ? 'Sending...'
              : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend code'}
          </button>
        </div>
      </Card>
    </div>
  );
};

OTPVerification.displayName = 'OTPVerification';

export default OTPVerification;
